// Shared rules for the request form ("Erstgespräch anfragen"), used by the form and by /api/anfrage.

export const INQUIRY_LIMITS = {
    vorname: 80,
    email: 254,
    telefon: 40,
    coachingArt: 120,
    ziel: 80,
    ziele: 8,
    nachricht: 2000
} as const

// Anything faster than this after the form appeared is treated as automated.
export const MIN_FILL_TIME_MS = 2500

export const UNDECIDED_OPTION = 'Ich bin mir noch unsicher'

export interface InquiryInput {
    vorname: string
    email: string
    telefon: string
    coachingArt: string
    ziele: Array<string>
    nachricht: string
    datenschutz: boolean
    // Honeypot: invisible to people, filled in by bots
    website: string
    startedAt: number
}

export type InquiryField = 'vorname' | 'email' | 'telefon' | 'coachingArt' | 'ziele' | 'nachricht' | 'datenschutz'

export type InquiryErrors = Partial<Record<InquiryField, string>>

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/
const PHONE_PATTERN = /^[+0-9][0-9 ()/.-]{4,}$/
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g

const toText = (value: unknown, max: number, multiline = false): string => {
    if (typeof value !== 'string') return ''

    const cleaned = value.replace(CONTROL_CHARACTERS, '')
    const flattened = multiline ? cleaned.replace(/\r\n?/g, '\n') : cleaned.replace(/\s+/g, ' ')

    return flattened.trim().slice(0, max + 1)
}

// Turns any request body into a well-formed input object (types and lengths only, no judgement yet).
export const normalizeInquiry = (body: unknown): InquiryInput => {
    const source = typeof body === 'object' && body !== null ? body as Record<string, unknown> : {}
    const ziele = Array.isArray(source.ziele) ? source.ziele : []

    return {
        vorname: toText(source.vorname, INQUIRY_LIMITS.vorname),
        email: toText(source.email, INQUIRY_LIMITS.email).toLowerCase(),
        telefon: toText(source.telefon, INQUIRY_LIMITS.telefon),
        coachingArt: toText(source.coachingArt, INQUIRY_LIMITS.coachingArt),
        ziele: ziele
            .map(entry => toText(entry, INQUIRY_LIMITS.ziel))
            .filter(entry => entry !== '')
            .slice(0, INQUIRY_LIMITS.ziele + 1),
        nachricht: toText(source.nachricht, INQUIRY_LIMITS.nachricht, true),
        datenschutz: source.datenschutz === true,
        website: toText(source.website, 200),
        startedAt: typeof source.startedAt === 'number' && Number.isFinite(source.startedAt) ? source.startedAt : 0
    }
}

export const validateInquiry = (input: InquiryInput): InquiryErrors => {
    const errors: InquiryErrors = {}

    if (input.vorname === '') errors.vorname = 'Bitte gib deinen Vornamen an.'
    else if (input.vorname.length > INQUIRY_LIMITS.vorname) errors.vorname = 'Bitte kürze deinen Vornamen.'

    if (input.email === '') errors.email = 'Bitte gib deine E-Mail-Adresse an.'
    else if (input.email.length > INQUIRY_LIMITS.email || !EMAIL_PATTERN.test(input.email)) errors.email = 'Bitte gib eine gültige E-Mail-Adresse an.'

    if (input.telefon !== '' && (input.telefon.length > INQUIRY_LIMITS.telefon || !PHONE_PATTERN.test(input.telefon))) {
        errors.telefon = 'Bitte gib eine gültige Telefonnummer an oder lass das Feld leer.'
    }

    if (input.coachingArt === '') errors.coachingArt = 'Bitte wähle aus, welche Betreuung dich interessiert.'
    else if (input.coachingArt.length > INQUIRY_LIMITS.coachingArt) errors.coachingArt = 'Bitte wähle eine der Optionen.'

    if (input.ziele.length > INQUIRY_LIMITS.ziele) errors.ziele = 'Bitte wähle höchstens acht Ziele aus.'

    if (input.nachricht.length > INQUIRY_LIMITS.nachricht) errors.nachricht = 'Bitte fasse dich etwas kürzer, höchstens 2000 Zeichen.'

    if (!input.datenschutz) errors.datenschutz = 'Bitte bestätige den Hinweis zum Datenschutz.'

    return errors
}
